import React, { useEffect, useRef, useState } from "react";
import CircularProgress from "component/misc/CircularProgress.jsx";
import useBreakpoint from "utils/useBreakPoint.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { timeAgo } from "utils/timeAgo.js";
import { translateSectorLabel } from "utils/sectorLabel";

const AllNews = ({
  title,
  summary,
  _id,
  sectors = [],
  created_at,
  reading_time_minutes,
  progress,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const cardRef = useRef(null);
  const [isCompactWidth, setIsCompactWidth] = useState(false);
  const breakpoint = useBreakpoint();
  const stackLayout = breakpoint === "mobile" || isCompactWidth;
  const hasMeta =
    (_id !== undefined && _id !== null) ||
    Boolean(created_at) ||
    (typeof reading_time_minutes !== "undefined" && reading_time_minutes !== null) ||
    (typeof progress !== "undefined" && progress !== null);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    if (!cardRef.current) return;
    const el = cardRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect?.width ?? el.clientWidth;
        setIsCompactWidth(width < 400);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleClick = (e) => {
    e.preventDefault();
    if (_id !== undefined && _id !== null) {
      navigate(`/news/news-view/${_id}`);
    } else {
      navigate(`/news/matching-news?q=${title}&type=ai`);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && _id !== undefined && _id !== null) {
      e.preventDefault();
      navigate(`/news/news-view/${_id}`);
    }
  };

  return (
    <div
      ref={cardRef}
      className="news_comment_card"
      onClick={handleClick}
      style={{ cursor: _id ? "pointer" : "default" }}
    >
      <h6 className="comment_title fw-semibold">{title || t("news.untitled")}</h6>
      <p className="text-white fw-light comment_description">
        {summary || ""}
      </p>

      {stackLayout ? (
        <div className="news_detail d-flex flex-column gap-2">
          {hasMeta && (
            <div className="category_wrap d-flex align-items-center justify-content-between small w-100">
              <p className="text-white mb-0 d-flex align-items-center gap-2">
                <span className="active_news"></span> {timeAgo(created_at)}
              </p>
              <div className="d-flex align-items-center gap-3">
                <span className="post_time">{reading_time_minutes} {t("news.readTime.min")}</span>
                <CircularProgress progress={progress} />
              </div>
            </div>
          )}
          {sectors && sectors.slice(0, 2).map((sector, idx) => (
            <div className="category_wrap w-100" key={idx}>
              <span className="category text-white rounded-4 d-block w-100 text-center">{translateSectorLabel(sector, i18n.resolvedLanguage || i18n.language)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="news_detail d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="category_wrap d-flex gap-2 align-items-center small">
            {hasMeta && (
              <p className="text-white mb-0">
                <span className="active_news"></span> {timeAgo(created_at)}
              </p>
            )}
            {sectors && sectors.slice(0, 2).map((sector, idx) => (
              <span key={idx} className="category rounded-4 text-white">{translateSectorLabel(sector, i18n.resolvedLanguage || i18n.language)}</span>
            ))}
          </div>
          {hasMeta && (
            <div className="category_wrap d-flex align-items-center gap-3 small">
              <span className="post_time">{reading_time_minutes} {t("news.readTime.min")}</span>
              <CircularProgress progress={progress} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AllNews;
